import { useState, useEffect } from 'react';
import { 
  User, Shield, Bell, Database, Bot, 
  Settings as SettingsIcon, Building2, CalendarX, 
  UserRound, ClipboardList, Stethoscope, Tag, 
  Wallet, FileEdit, FileText, Volume2, ChevronRight
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Insurances from './Insurances';
import { GoogleGenerativeAI } from "@google/generative-ai";
import './Settings.css';

import GeneralSettings from './GeneralSettings';
import AppointmentReasons from './AppointmentReasons';
import Holidays from './Holidays';
import Studies from './Studies';
import ConsultoriosSettings from './ConsultoriosSettings';

const Settings = () => {
  const { addNotification } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Tabs grouping for better organization
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'profile');
  
  // Perfil Admin Settings
  const savedProfile = (() => {
    try {
      const item = localStorage.getItem('gdt_admin_profile');
      const parsed = item ? JSON.parse(item) : {};
      return parsed || {};
    } catch(e) { return {}; }
  })();
  const [profileData, setProfileData] = useState({
    fullName: savedProfile.fullName || 'Gestion de turnos',
    email: savedProfile.email || 'info@gestionamientodeturnos.com',
    specialty: savedProfile.specialty || '.',
    registration: savedProfile.registration || '.'
  });
  
  const handleSaveProfile = () => {
    localStorage.setItem('gdt_admin_profile', JSON.stringify(profileData));
    addNotification({ title: 'Perfil Actualizado', message: 'Los datos se guardaron correctamente.', type: 'success' });
  };
  const [apiKey, setApiKey] = useState(localStorage.getItem('gdt_ai_key') || '');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState('');

  useEffect(() => {
    const fetchAiSettings = async () => {
      try {
        const { data } = await supabase
          .from('system_settings')
          .select('key, value')
          .in('key', ['gdt_ai_key', 'gdt_ai_model']);

        if (data && data.length > 0) {
          const keyRow = data.find(r => r.key === 'gdt_ai_key');
          const modelRow = data.find(r => r.key === 'gdt_ai_model');
          if (keyRow?.value) {
            setApiKey(keyRow.value);
            localStorage.setItem('gdt_ai_key', keyRow.value);
          }
          if (modelRow?.value) {
            localStorage.setItem('gdt_ai_model', modelRow.value);
          }
        }
      } catch (err) {
        console.warn('[Settings] Failed to fetch AI settings from Supabase:', err);
      }
    };
    fetchAiSettings();
  }, []);

  const handleSaveAI = async () => {
    const trimmedKey = apiKey.trim();
    if (!trimmedKey) {
      addNotification('Por favor, introducí una API Key.', 'error');
      return;
    }

    if (trimmedKey === 'demo') {
      localStorage.setItem('gdt_ai_key', 'demo');
      try {
        await supabase.from('system_settings').upsert([
          { key: 'gdt_ai_key', value: 'demo' }
        ], { onConflict: 'key' });
      } catch (err) {
        console.warn('Error upserting demo key to system_settings:', err);
      }
      addNotification('Modo DEMO activado. La IA simulará respuestas.', 'info');
      return;
    }

    setIsAiLoading(true);
    setAiStatus('INICIANDO...');

    try {
      const genAI = new GoogleGenerativeAI(trimmedKey);
      
      const modelsToTry = [
        "gemini-2.0-flash", 
        "gemini-flash-latest", 
        "gemini-1.5-flash",
        "gemini-pro-latest",
        "gemini-1.5-pro"
      ];
      
      let success = false;
      let workingModel = "";
      let lastError = "";
      
      for (const mName of modelsToTry) {
        try {
          setAiStatus(`PROBANDO ${mName.toUpperCase()}...`);
          const model = genAI.getGenerativeModel({ model: mName });
          await model.generateContent("Hola");
          success = true;
          workingModel = mName;
          break;
        } catch (e) {
          console.warn(`[GDT SETTINGS] Falló ${mName}:`, e.message);
          lastError = e.message;
          await new Promise(r => setTimeout(r, 500));
        }
      }

      if (!success) {
        let cleanError = 'No se pudo conectar con los servidores de Gemini AI.';
        if (lastError?.includes('API key not valid') || lastError?.includes('API_KEY_INVALID') || lastError?.includes('400')) {
          cleanError = 'La API Key ingresada no es válida. Verifícala en Google AI Studio o escribe "demo" para activar el modo simulación.';
        } else if (lastError?.includes('quota') || lastError?.includes('RESOURCE_EXHAUSTED')) {
          cleanError = 'Se ha superado la cuota de la API Key. Por favor intenta más tarde.';
        } else {
          cleanError = `Error de conexión con Gemini: ${lastError ? lastError.substring(0, 100) : 'Verifica tu conexión.'}`;
        }
        addNotification(cleanError, 'error');
        return;
      }
      
      localStorage.setItem('gdt_ai_key', trimmedKey);
      localStorage.setItem('gdt_ai_model', workingModel);
      localStorage.setItem('gdt_ai_version', 'v1'); // Default fallback version string

      try {
        await supabase.from('system_settings').upsert([
          { key: 'gdt_ai_key', value: trimmedKey },
          { key: 'gdt_ai_model', value: workingModel }
        ], { onConflict: 'key' });
      } catch (dbErr) {
        console.error('Error saving AI key to Supabase system_settings:', dbErr);
      }

      addNotification(`¡Conexión exitosa! Usando ${workingModel.toUpperCase()}.`, 'success');
    } catch (err) {
      addNotification(`Error inesperado: ${err.message}`, 'error');
    } finally {
      setIsAiLoading(false);
      setAiStatus('');
    }
  };

  const menuGroups = [
    {
      title: "CUENTA",
      items: [
        { id: 'profile', label: 'Mi Perfil', icon: User },
        { id: 'security', label: 'Seguridad', icon: Shield },
        { id: 'notifications', label: 'Notificaciones', icon: Bell },
      ]
    },
    {
      title: "CONFIGURACIÓN MÉDICA",
      items: [
        { id: 'general-med', label: 'Configuraciones Varias', icon: SettingsIcon },
        { id: 'insurances', label: 'Obras Sociales', icon: Building2 },
        { id: 'motives', label: 'Motivos de Turno', icon: ClipboardList },
        { id: 'services', label: 'Tipos de Turno / Servicio', icon: Stethoscope },
        { id: 'holidays', label: 'Feriados y No Laborables', icon: CalendarX },
        { id: 'patient-types', label: 'Tipos de Pacientes', icon: UserRound },
        { id: 'tags', label: 'Tags de Turnos', icon: Tag },
      ]
    },
    {
      title: "PLANTILLAS Y DOCUMENTOS",
      items: [
        { id: 'templates-evol', label: 'Plantillas de Evolución', icon: FileEdit },
        { id: 'templates-orders', label: 'Plantillas de Órdenes', icon: FileText },
      ]
    },
    {
      title: "SISTEMA Y AVANZADO",
      items: [
        { id: 'cash-types', label: 'Tipos de Caja', icon: Wallet },
        { id: 'caller', label: 'Llamador a Consultorios', icon: Volume2 },
        { id: 'ai', label: 'NEON AI Copilot', icon: Bot, special: true },
        { id: 'data', label: 'Datos y Backup', icon: Database },
      ]
    }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="settings-section animate-fade">
            <h3>Información del Profesional</h3>
            <p className="section-desc">Gestioná tus datos personales y de contacto para los pacientes.</p>
            <div className="form-grid">
              <div className="form-group">
                <label>NOMBRE COMPLETO</label>
                <input type="text" value={profileData.fullName} onChange={(e) => setProfileData({...profileData, fullName: e.target.value})} />
              </div>
              <div className="form-group">
                <label>CORREO ELECTRÓNICO PROFESIONAL</label>
                <input type="email" value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})} />
              </div>
              <div className="form-group">
                <label>ESPECIALIDAD PRINCIPAL</label>
                <input type="text" value={profileData.specialty} onChange={(e) => setProfileData({...profileData, specialty: e.target.value})} />
              </div>
              <div className="form-group">
                <label>MATRÍCULA NACIONAL</label>
                <input type="text" value={profileData.registration} onChange={(e) => setProfileData({...profileData, registration: e.target.value})} />
              </div>
            </div>
            <button className="btn-primary" style={{ marginTop: '2rem' }} onClick={handleSaveProfile}>GUARDAR CAMBIOS</button>
          </div>
        );
      
      case 'general-med':
        return <GeneralSettings />;

      case 'insurances':
        return <Insurances />;

      case 'motives':
        return <AppointmentReasons />;

      case 'services':
        return <Studies />;

      case 'holidays':
        return <Holidays />;

      case 'caller':
        return <ConsultoriosSettings />;

      case 'ai':
        return (
          <div className="settings-section ai-config-neon animate-fade">
            <div className="ai-neon-header">
              <div className="ai-neon-icon">
                <Bot size={32} />
              </div>
              <div className="ai-neon-title">
                <h2>NEON AI Copilot</h2>
                <p>Potenciá tu CRM con inteligencia artificial de Google Gemini.</p>
              </div>
            </div>
            
            <div className="ai-neon-grid">
              <div className="ai-neon-form">
                <div className="form-group">
                  <label className="neon-label">GOOGLE GEMINI API KEY</label>
                  <div className="neon-api-input-wrapper">
                    <input 
                      type="password" 
                      placeholder="••••••••••••••••••••••••••••••••••••••" 
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                    />
                    <button 
                      className="btn-neon-save" 
                      onClick={handleSaveAI}
                      disabled={isAiLoading}
                    >
                      {isAiLoading ? (aiStatus || 'PROBANDO...') : 'PROBAR Y GUARDAR'}
                    </button>
                  </div>
                  <p className="neon-help-text">
                    * Tu clave se guarda solo en este navegador. Podés obtener una gratis en <a href="https://aistudio.google.com/" target="_blank" rel="noreferrer">Google AI Studio</a>.
                  </p>
                </div>
              </div>

              <div className="ai-neon-info-card">
                <h4>Funciones Disponibles 🤖</h4>
                <ul>
                  <li>Análisis de tendencias de ausentismo.</li>
                  <li>Sugerencia de horarios para sobreturnos.</li>
                  <li>Redacción automática de reportes médicos.</li>
                  <li>Asistente de agendamiento por lenguaje natural.</li>
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="settings-section animate-fade placeholder-section">
            <div className="placeholder-icon"><SettingsIcon size={48} /></div>
            <h3>{activeTab.replace(/-/g, ' ').toUpperCase()}</h3>
            <p>Este módulo está siendo optimizado para la estética GDT v2.0.</p>
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>VOLVER AL DASHBOARD</button>
          </div>
        );
    }
  };


  return (
    <div className="settings-page">
      <div className="settings-container">
        <aside className="settings-sidebar card">
          <div className="sidebar-scroll">
            {menuGroups.map((group, gIdx) => (
              <div key={gIdx} className="settings-group">
                <h4 className="group-title">{group.title}</h4>
                <div className="group-items">
                  {group.items.map((item) => (
                    <button 
                      key={item.id}
                      className={`settings-nav-item ${activeTab === item.id ? 'active' : ''} ${item.special ? 'ai-tab' : ''}`}
                      onClick={() => setActiveTab(item.id)}
                    >
                      <item.icon size={18} />
                      <span className="nav-label">{item.label}</span>
                      {activeTab === item.id && <ChevronRight size={14} className="active-arrow" />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>
        
        <main className="settings-main card">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default Settings;
